import { useMemo, useState, type ChangeEvent } from 'react';
import {
  aplicarPremioPorTotal, avisoDeMonto, canjesDisponibles, describirRegla, esEquivalencia, etiquetaUnidad, excedeTope,
  faltaPremio, premiosSugeridos, resumenPorUnidad,
  type DinamicaConfig, type LineaCanje, type PremioEntregado,
} from '../../domain/canjes';
import { enqueueCanje } from '../../offline/canjeQueue';
import { compressImage } from '../../lib/compressImage';
import CampoFoto from '../../components/CampoFoto';
import type { ConfigCanjes } from '../../api/canjes';

/** De dónde salen la campaña, la tienda y el día: de la asignación, no de una pregunta. */
export interface ContextoCanje {
  campaignId: string;
  pdvId: string;
  /** `null` cuando no había programación: el servidor marca el registro como fuera de ella. */
  assignmentId: string | null;
  fecha: string;
}

/** Una línea en edición: la dinámica, sus canjes y los premios (con su modo de edición). */
interface LineaEnEdicion {
  dynamicId: string;
  canjes: number;
  /** Premios elegidos a mano. Vacío = se usan los sugeridos por la dinámica. */
  premiosManuales: PremioEntregado[] | null;
}

const MARCAS = [
  'Scott', 'Suave', 'Paracas', 'Huggies', 'Kotex', 'Pampers', 'Familia', 'Always', 'Nosotras',
  'Otro (mencionar en observaciones)',
];

const lineaVacia = (): LineaEnEdicion => ({ dynamicId: '', canjes: 1, premiosManuales: null });

/**
 * Comprime la foto **al capturarla**, no al subirla: la cola local la guarda en el celular
 * mientras no hay señal, y una jornada de 20 canjes con dos fotos sin comprimir cada uno
 * llenaría el almacenamiento del navegador. Si la compresión falla (un dispositivo raro, un
 * canvas no disponible), se usa la original: no poder comprimir nunca debe impedir registrar.
 */
async function prepararFoto(file: File): Promise<Blob> {
  try {
    return await compressImage(file);
  } catch {
    return file;
  }
}

/**
 * Formulario de UN canje = UN cliente, con una línea por dinámica.
 *
 * Es el cambio de fondo contra DataScope, donde cada canje era una respuesta y un cliente con
 * cuatro canjes salía cuatro veces (caso real: Efraín Velásquez, 10-jul, 19:52/19:54/19:56).
 *
 * Todo se guarda en la cola local, incluso con señal: un solo camino tiene un solo conjunto de
 * errores posibles, y es el mismo camino que ya funciona sin conexión.
 */
export default function CanjeForm({ config, contexto, onGuardado, onCancelar }: {
  config: ConfigCanjes;
  contexto: ContextoCanje;
  onGuardado: (nombreCliente: string) => void;
  onCancelar: () => void;
}) {
  const [cliente, setCliente] = useState('');
  const [contacto, setContacto] = useState('');
  const [monto, setMonto] = useState('');
  const [lineas, setLineas] = useState<LineaEnEdicion[]>(() => {
    // Modo simple: la única dinámica ya viene puesta con su único canje, así que la impulsadora
    // abre el formulario y lo primero que ve es la pregunta que de verdad tiene que contestar.
    const d =
      config.dinamicas.length === 1 && config.maxPerClient === 1 ? config.dinamicas[0] : null;
    return d ? [{ dynamicId: d.id, canjes: 1, premiosManuales: [] }] : [lineaVacia()];
  });
  const [marcas, setMarcas] = useState<string[]>([]);
  const [observaciones, setObservaciones] = useState('');
  const [boleta, setBoleta] = useState<Blob | null>(null);
  const [numeroBoleta, setNumeroBoleta] = useState('');
  const [premio, setPremio] = useState<Blob | null>(null);
  const [error, setError] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [guardando, setGuardando] = useState(false);

  const porId = useMemo(
    () => new Map(config.dinamicas.map((d) => [d.id, d])),
    [config.dinamicas],
  );

  /**
   * Premio por el TOTAL del canje (0386, Takis Mercados: «cada 3 tiras = 1 Licua-Takis, máximo 3»). Con regla no se
   * pregunta premio por línea: lo calcula el sistema sumando todas las líneas.
   */
  const regla = config.premioPorTotal;
  const unidad = etiquetaUnidad(config.unidadPlural);

  /** Las líneas listas para las reglas del dominio: con sus premios ya resueltos. */
  const lineasResueltas: LineaCanje[] = useMemo(() => {
    const base = lineas
      .filter((l) => l.dynamicId !== '')
      .map((l) => {
        const d = porId.get(l.dynamicId) as DinamicaConfig;
        return {
          dynamicId: l.dynamicId,
          canjes: l.canjes,
          premios: l.premiosManuales ?? premiosSugeridos(d, l.canjes),
        };
      });
    // Con combinaciones (0407, picantes y cajas) cada línea suma a la bolsa de su dinámica.
    return regla === null ? base : aplicarPremioPorTotal(base, regla, (id) => porId.get(id)?.grupo);
  }, [lineas, porId, regla]);

  const usados = lineasResueltas.reduce((s, l) => s + l.canjes, 0);
  const disponibles = canjesDisponibles(lineasResueltas, config.maxPerClient);
  const montoNumero = monto.trim() === '' ? null : Number(monto);

  /**
   * ¿Se le pide el nombre al cliente? Basta que UNA de las dinámicas del día lo pida.
   *
   * Se mira la configuración de la campaña y no la línea que se está llenando, porque el campo
   * está arriba del todo: si apareciera y desapareciera al cambiar de dinámica, el nombre ya
   * escrito se perdería de vista sin explicación. Con dinámicas mixtas se pide, que es el lado
   * seguro: sobra un dato en vez de faltar la identificación de quien recibió el producto.
   */
  const pideNombre = config.dinamicas.some((d) => d.requiresClientName);

  /**
   * MODO SIMPLE: una sola dinámica vigente y un canje por boleta.
   *
   * Es el caso del raspa y gana, y no se parece en nada al canje multimarca. Ahí el cliente
   * llega con una compra grande y se le arma un registro con varias líneas; aquí la impulsadora
   * está de pie frente a alguien que acaba de volver de caja con su boleta, y **la promoción es
   * una sola y da un premio, compre S/24 o S/200**. Preguntarle qué dinámica eligió —si solo hay
   * una—, cuántos canjes usó —si siempre es uno— o si quiere agregar otra dinámica es hacerle
   * contestar tres veces lo mismo delante del cliente.
   *
   * Se decide por los datos y no por la campaña: cualquier promoción de una dinámica con tope de
   * uno se comporta así, sin tocar el código.
   */
  const modoSimple = config.dinamicas.length === 1 && config.maxPerClient === 1;

  /**
   * Marcas de la competencia, de la configuración de la campaña (0031).
   * Estaban CABLEADAS con las del piloto Softys, así que en la dinámica de un suavizante la
   * impulsadora veía papel higiénico y pañales: la pregunta se volvía absurda y el dato, basura.
   * Sin marcas configuradas, la pregunta no se muestra — mejor no preguntar que preguntar mal.
   */
  const marcasDisponibles = config.competitorBrands.length > 0 ? config.competitorBrands : MARCAS;

  /**
   * ¿El premio elegido pide un número de contacto? (0032)
   *
   * No está cableado a "Lavaseca": es una marca del premio en la configuración. Los premios
   * chicos se entregan en el acto y no hace falta ubicar a nadie; uno grande que no se entrega
   * ahí mismo sí, y sin un teléfono el ganador se pierde. El campo aparece SOLO al elegirlo,
   * para no pedirle el número a las decenas de personas que se llevan un paño.
   */
  const premioElegidoId = (lineas[0]?.premiosManuales ?? [])[0]?.prizeId;
  const pideContacto = config.premios.some((p) => p.id === premioElegidoId && p.requiresContact);

  function cambiarLinea(i: number, cambio: Partial<LineaEnEdicion>): void {
    setLineas((ls) => ls.map((l, j) => (j === i ? { ...l, ...cambio } : l)));
    setError('');
    setConfirmacion('');
  }

  /** Abre la edición de premios precargando lo sugerido: así corregir es ajustar, no rellenar. */
  function abrirStock(i: number): void {
    const l = lineas[i];
    const d = porId.get(l.dynamicId);
    if (!d) return;
    cambiarLinea(i, { premiosManuales: premiosSugeridos(d, l.canjes) });
  }

  function cambiarUnidades(i: number, prizeId: string, units: number): void {
    setLineas((ls) => ls.map((l, j) => {
      if (j !== i) return l;
      const actuales = l.premiosManuales ?? [];
      const sinEse = actuales.filter((p) => p.prizeId !== prizeId);
      return {
        ...l,
        premiosManuales: units > 0 ? [...sinEse, { prizeId, units }] : sinEse,
      };
    }));
    setError('');
  }

  async function elegirFoto(e: ChangeEvent<HTMLInputElement>, cual: 'boleta' | 'premio') {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const blob = await prepararFoto(file);
    if (cual === 'boleta') setBoleta(blob); else setPremio(blob);
  }

  /** Qué impide guardar. Devuelve el mensaje, o `null` si se puede. */
  function loQueFalta(): string | null {
    if (pideNombre && cliente.trim() === '') return 'Falta el nombre del cliente.';
    if (lineasResueltas.length === 0) return 'Elige al menos una dinámica.';
    if (lineasResueltas.some((l) => l.canjes < 1)) return 'Cada dinámica necesita al menos 1 canje.';
    // Con premio elegido el mensaje nombra la dinámica, que es lo que la persona ve en pantalla
    // cuando hay más de una línea. `faltaPremio` mira las unidades, no la longitud de la lista:
    // el selector deja filas con 0 unidades y una lista de ceros no es un premio entregado.
    //
    // Con regla por total (0386) no hay premio que preguntar: una venta de 1 o 2 tiras va sin premio y es válida.
    if (regla === null) {
      const sinPremio = faltaPremio(lineasResueltas, config.dinamicas);
      if (sinPremio !== null) return sinPremio;
      if (lineasResueltas.some((l) => l.premios.length === 0)) {
        return 'Falta indicar el premio entregado.';
      }
    }
    if (excedeTope(lineasResueltas, config.maxPerClient)) {
      return `Este cliente llegó al máximo de ${config.maxPerClient} canjes: `
        + `llevas ${usados}. Quita canjes o registra el resto en otro cliente.`;
    }
    // La foto de la boleta ya NO bloquea (0036): lo único que se exige es el premio, que es el
    // dato que no se puede reconstruir después. La boleta la conserva el cliente y la entrega la
    // recuerda la impulsadora, pero qué salió en la raspadita no lo sabe nadie más.
    return null;
  }

  async function guardar(saltarAviso = false): Promise<void> {
    setError('');
    const falta = loQueFalta();
    if (falta !== null) { setError(falta); return; }

    // El monto AVISA, no bloquea: un monto raro es mala calidad de dato, no producto no
    // autorizado. La decisión la toma ella, con el número a la vista.
    if (!saltarAviso) {
      const aviso = avisoDeMonto(montoNumero, lineasResueltas, config.dinamicas);
      if (aviso !== null) { setConfirmacion(aviso); return; }
    }

    setGuardando(true);
    try {
      // Un solo uuid para la clave de la cola y para el payload: es lo que hace idempotente el
      // reintento. La cola además lo vuelve a forzar al enviar, para que no puedan divergir.
      const clientUuid = crypto.randomUUID();
      await enqueueCanje({
        clientUuid,
        input: {
          clientUuid,
          campaignId: contexto.campaignId,
          pdvId: contexto.pdvId,
          assignmentId: contexto.assignmentId,
          fecha: contexto.fecha,
          ocurridoEn: null,
          // Sin nombre pedido va `null`, no cadena vacía: la 0030 relajó la columna a
          // nullable pero conservó el CHECK de "si viene, que sea un nombre". Mandar '' sería
          // un rechazo del servidor por un dato que nadie pidió.
          clienteNombre: pideNombre ? cliente.trim() : null,
          clienteContacto: contacto.trim() === '' ? null : contacto.trim(),
          montoCompra: montoNumero,
          marcasDejadas: marcas,
          observaciones: observaciones.trim() === '' ? null : observaciones.trim(),
          // Vacío va `null`, no cadena vacía: "no lo anotó" y "anotó nada" no son estados
          // distintos, y en la bandeja una cadena vacía se vería como un número en blanco.
          boletaNumero: numeroBoleta.trim() === '' ? null : numeroBoleta.trim(),
          lineas: lineasResueltas.map((l) => ({
            dynamicId: l.dynamicId,
            canjes: l.canjes,
            premios: l.premios.map((p) => ({
              prizeId: p.prizeId,
              units: p.units,
              // La equivalencia se CALCULA comparando con lo esperado, no se pregunta. Con regla por total el premio
              // lo calculó el sistema y nunca es una equivalencia por falta de stock.
              equivalencia: regla === null && esEquivalencia(l, porId.get(l.dynamicId) as DinamicaConfig),
            })),
          })),
        },
        boletaBlob: boleta,
        premioBlob: premio,
      });
      onGuardado(cliente.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar el canje. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-4 p-4">
      <h1 className="text-xl font-semibold">Registrar canje</h1>

      {pideNombre && (
        <label className="block">
          <span className="text-sm text-gray-700">Cliente</span>
          <input
            value={cliente}
            onChange={(e) => { setCliente(e.target.value); setError(''); }}
            className="mt-1 block w-full rounded border px-3 py-2"
          />
        </label>
      )}

      {!modoSimple && (
      <label className="block">
        <span className="text-sm text-gray-700">Contacto (opcional)</span>
        <input
          value={contacto}
          onChange={(e) => setContacto(e.target.value)}
          inputMode="tel"
          className="mt-1 block w-full rounded border px-3 py-2"
        />
      </label>
      )}

      {!modoSimple && (
      <label className="block">
        <span className="text-sm text-gray-700">Monto de compra (S/)</span>
        <input
          value={monto}
          onChange={(e) => { setMonto(e.target.value); setError(''); setConfirmacion(''); }}
          inputMode="decimal"
          className="mt-1 block w-full rounded border px-3 py-2"
        />
      </label>
      )}

      <section className={modoSimple ? '' : 'rounded border p-3'}>
        {!modoSimple && (
          <div className="mb-2 flex items-baseline justify-between">
            <h2 className="font-medium">Canjes de este cliente</h2>
            {config.maxPerClient !== null && (
              <span className="text-sm text-gray-600">{usados} de {config.maxPerClient}</span>
            )}
          </div>
        )}

        {lineas.map((l, i) => {
          const d = porId.get(l.dynamicId);
          const sugeridos = d ? premiosSugeridos(d, l.canjes) : [];
          const nombreDe = (id: string) =>
            config.premios.find((p) => p.id === id)?.name ?? '';
          // En una dinámica de premio ELEGIDO (un raspa y gana) el premio no se puede sugerir:
          // lo decidió la raspadita. Se pregunta siempre, y elegirlo NO es una equivalencia por
          // falta de stock, así que ni el aviso ámbar ni el botón de "cambiar" tienen sentido.
          const eligePremio = d?.prizeSelection === 'elegido';
          return (
            <div key={i} className={modoSimple ? '' : 'mb-3 space-y-2 border-b pb-3 last:border-b-0'}>
              {!modoSimple && (
              <label className="block">
                <span className="text-sm text-gray-700">Dinámica</span>
                <select
                  value={l.dynamicId}
                  onChange={(e) => cambiarLinea(i, {
                    dynamicId: e.target.value,
                    // Con premio elegido se entra directo al selector (lista vacía, no `null`):
                    // si quedara en `null` se mostraría "Premio: " sin nada y un botón que habla
                    // de falta de stock, y no habría forma de decir qué salió en la raspadita.
                    premiosManuales:
                      porId.get(e.target.value)?.prizeSelection === 'elegido' ? [] : null,
                  })}
                  className="mt-1 block w-full rounded border px-3 py-2"
                >
                  <option value="">— Elige la dinámica —</option>
                  {config.dinamicas.map((din) => (
                    <option key={din.id} value={din.id}>{din.name}</option>
                  ))}
                </select>
              </label>
              )}

              {!modoSimple && (
              <label className="block">
                {/* Cada línea con SU unidad (0407): una de cajas rotulada «Tiras» se llenaría mal. */}
                <span className="text-sm text-gray-700">{d?.unidad ? etiquetaUnidad(d.unidad) : unidad}</span>
                <input
                  type="number"
                  min={1}
                  value={l.canjes}
                  onChange={(e) => cambiarLinea(i, {
                    canjes: Number(e.target.value),
                    premiosManuales: eligePremio ? l.premiosManuales : null,
                  })}
                  className="mt-1 block w-24 rounded border px-3 py-2"
                />
              </label>
              )}

              {d && !eligePremio && l.premiosManuales === null && regla === null && (
                <div className="text-sm">
                  <p className="text-gray-700">
                    Premio:{' '}
                    {sugeridos.map((p) => `${nombreDe(p.prizeId)} ×${p.units}`).join(' + ')}
                  </p>
                  <button
                    type="button"
                    onClick={() => abrirStock(i)}
                    className="mt-1 min-h-[44px] text-blue-700 underline"
                  >
                    cambiar por falta de stock
                  </button>
                </div>
              )}

              {d && eligePremio && modoSimple ? (
                /* UN premio, UNA unidad: botones y no campos numéricos.
                   La raspadita da un premio; poner "unidades de cada premio" invita a escribir
                   cantidades que no existen y obliga a teclear donde basta con tocar. Elegir uno
                   deselecciona el anterior, así que es imposible registrar dos. */
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-800">
                    ¿Qué premio se llevó? <span className="text-red-600">*</span>
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {config.premios.map((p) => {
                      const elegido = (l.premiosManuales ?? []).some((x) => x.prizeId === p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          aria-pressed={elegido}
                          onClick={() => cambiarLinea(i, {
                            premiosManuales: elegido ? [] : [{ prizeId: p.id, units: 1 }],
                          })}
                          className={`min-h-[72px] rounded-xl border-2 px-2 py-3 text-sm font-semibold ${
                            elegido
                              ? 'border-indigo-600 bg-indigo-600 text-white'
                              : 'border-gray-300 bg-white text-gray-800'
                          }`}
                        >
                          {p.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : d && l.premiosManuales !== null && (
                <div className={`space-y-1 rounded p-2 ${eligePremio ? 'bg-blue-50' : 'bg-amber-50'}`}>
                  {eligePremio && (
                    <p className="text-sm font-medium text-gray-800">
                      ¿Qué premio se llevó? <span className="text-red-600">*</span>
                    </p>
                  )}
                  {config.premios.map((p) => (
                    <label key={p.id} className="flex items-center justify-between gap-2">
                      <span className="text-sm">Unidades de {p.name}</span>
                      <input
                        type="number"
                        min={0}
                        aria-label={`Unidades de ${p.name}`}
                        value={l.premiosManuales?.find((x) => x.prizeId === p.id)?.units ?? 0}
                        onChange={(e) => cambiarUnidades(i, p.id, Number(e.target.value))}
                        className="w-20 rounded border px-2 py-1"
                      />
                    </label>
                  ))}
                  {!eligePremio && (
                    <p className="text-xs text-amber-900">
                      Distinto a lo esperado — queda anotado (equivalente por stock)
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Con regla por total el premio se muestra calculado: la persona lo entrega con el número a la vista. */}
        {regla !== null && lineasResueltas.length > 0 && (
          <p className="mb-2 rounded bg-blue-50 px-3 py-2 text-sm text-blue-900">
            {resumenPorUnidad(lineasResueltas, (id) => porId.get(id)?.unidad, unidad.toLowerCase())} · entrega{' '}
            <strong>
              {lineasResueltas.reduce((s, l) => s + l.premios.reduce((t, p) => t + p.units, 0), 0)}{' '}
              {config.premios.find((p) => p.id === regla.prizeId)?.name ?? 'premio'}
            </strong>
            <span className="block text-xs">
              {describirRegla(regla, config.premios.find((p) => p.id === regla.prizeId)?.name ?? 'premio',
                unidad.toLowerCase())}
            </span>
          </p>
        )}

        {!modoSimple && (
          <>
            <button
              type="button"
              onClick={() => setLineas((ls) => [...ls, lineaVacia()])}
              className="min-h-[44px] text-blue-700 underline"
            >
              {regla === null ? '+ agregar otra dinámica' : '+ agregar otro producto'}
            </button>
            {disponibles !== null && (
              <p className="mt-1 text-sm text-gray-600">Le quedan {disponibles} canjes.</p>
            )}
          </>
        )}
      </section>


      {/* La boleta dejó de ser obligatoria (0036). Sigue yendo PRIMERO y con la cámara lista,
          porque es la evidencia que se quiere y es el orden real de la escena; pero hay tiendas
          que no dejan sacar el celular en sala, y ahí exigirla no producía una foto: producía un
          premio entregado que el sistema no podía registrar.

          Desde la 0043 la foto se puede OCULTAR por campaña y pedir el NÚMERO de boleta en su
          lugar — o las dos cosas, que son campos independientes. Misma razón de fondo: el número
          se apunta en papel y se tipea después; la foto necesita el celular en la mano. */}
      {config.pideFotoBoleta && (
        <CampoFoto
          etiqueta="Foto de la boleta (opcional)"
          nombre="la boleta"
          listaTexto="Foto de la boleta lista ✓"
          lista={boleta !== null}
          onElegir={(e) => void elegirFoto(e, 'boleta')}
        />
      )}

      {config.pideNumeroBoleta && (
        <label className="block">
          <span className="text-sm text-gray-700">Número de boleta (opcional)</span>
          <input
            value={numeroBoleta}
            onChange={(e) => setNumeroBoleta(e.target.value)}
            // `text` y no `number`: las series traen letras y guiones (`B001-00012345`), y el
            // teclado numérico dejaría a la impulsadora sin poder escribirlas.
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            placeholder="Ej.: B001-00012345"
            className="mt-1 block w-full rounded border px-3 py-3 text-base"
          />
        </label>
      )}

      <CampoFoto
        etiqueta="Foto de la entrega del premio (opcional)"
        nombre="el premio"
        listaTexto="Foto del premio lista ✓"
        lista={premio !== null}
        onElegir={(e) => void elegirFoto(e, 'premio')}
      />

      {modoSimple && (
        <label className="block">
          <span className="text-sm text-gray-700">Nombre del ganador (opcional)</span>
          <input
            value={cliente}
            onChange={(e) => { setCliente(e.target.value); setError(''); }}
            className="mt-1 block w-full rounded border px-3 py-2"
          />
        </label>
      )}

      {/* Aparece solo si el premio elegido lo pide: una lavaseca no se entrega en el acto y sin
          teléfono no hay cómo ubicar a quien la ganó. */}
      {modoSimple && pideContacto && (
        <label className="block">
          <span className="text-sm text-gray-700">
            Número de contacto del ganador
          </span>
          <input
            value={contacto}
            onChange={(e) => setContacto(e.target.value)}
            inputMode="tel"
            className="mt-1 block w-full rounded border px-3 py-2"
          />
          <span className="text-xs text-gray-500">
            Este premio no se entrega en el momento: hace falta para ubicarlo.
          </span>
        </label>
      )}

      {marcasDisponibles.length > 0 && (
        <fieldset className="rounded border p-3">
          <legend className="px-1 text-sm text-gray-700">¿Qué marca dejó de comprar?</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {marcasDisponibles.map((m) => (
              <label key={m} className="flex min-h-[44px] items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={marcas.includes(m)}
                  onChange={(e) => setMarcas((ms) =>
                    e.target.checked ? [...ms, m] : ms.filter((x) => x !== m))}
                />
                {m}
              </label>
            ))}
          </div>
        </fieldset>
      )}

      <label className="block">
        <span className="text-sm text-gray-700">Observaciones</span>
        <textarea
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          rows={2}
          className="mt-1 block w-full rounded border px-3 py-2"
        />
      </label>

      {error && <p className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      {confirmacion && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-medium">Antes de guardar, revisa esto:</p>
          <p className="mt-1">{confirmacion}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void guardar(true)}
              disabled={guardando}
              className="min-h-[44px] rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
            >
              Registrar igual
            </button>
            <button
              type="button"
              onClick={() => setConfirmacion('')}
              className="min-h-[44px] rounded border px-4 py-2"
            >
              Volver
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void guardar()}
          disabled={guardando}
          className="min-h-[44px] flex-1 rounded bg-blue-600 px-4 py-3 font-medium text-white disabled:opacity-50"
        >
          {guardando ? 'Guardando…' : 'Guardar canje'}
        </button>
        <button
          type="button"
          onClick={onCancelar}
          disabled={guardando}
          className="min-h-[44px] rounded border px-4 py-3"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}
